import { useAuth, API } from "../App";
import { useEffect, useState } from "react";
import axios from "axios";
import { Navigate, useNavigate } from "react-router-dom";
import { Calendar, Users, Clock, Sparkles, ArrowRight, Eye } from "lucide-react";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";

const Landing = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null); // null until API responds — prevents hardcoded placeholder flash

  useEffect(() => {
    axios.get(`${API}/subscription/config`).then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  const symbol = (c) => ({ usd: "$", aud: "A$", eur: "€", gbp: "£", cad: "C$", nzd: "NZ$" }[c] || "$");
  const fmtPrice = (p) => `${symbol(pricing?.currency)}${Number(p).toFixed(Number.isInteger(Number(p)) ? 0 : 2)}`;

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const features = [
    {
      icon: Sparkles,
      title: "AI-Powered Scheduling",
      description: "GPT-5.2 generates optimal rosters considering all your rules and preferences"
    },
    {
      icon: Users,
      title: "Employee Management",
      description: "Track skills, preferences, and availability for each team member"
    },
    {
      icon: Clock,
      title: "Flexible Shift Types",
      description: "Create custom shifts or use standard morning, afternoon, and night shifts"
    },
    {
      icon: Calendar,
      title: "Time-Off Requests",
      description: "Manage and approve time-off requests that are automatically considered"
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size={32} />
          <div className="flex items-center gap-2">
            <a
              href="/preview"
              className="px-3 py-2 text-sm text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100 rounded inline-flex items-center gap-1.5 transition-colors"
              data-testid="header-preview-link"
            >
              <Eye className="w-4 h-4" /> See it in action
            </a>
            <Button
              onClick={handleLogin}
              className="bg-[#002FA7] hover:bg-[#00227A] text-white font-medium rounded"
              data-testid="header-login-btn"
            >
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 lg:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#002FA7] mb-4">
              AI-POWERED ROSTER MANAGEMENT
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-zinc-900 mb-6">
              Build smarter rosters with artificial intelligence
            </h2>
            <p className="text-lg text-zinc-600 mb-8 leading-relaxed">
              Define your rules, add your team, and let GPT-5.2 generate optimal schedules. 
              Respects time-off requests, skill requirements, and fair distribution automatically.
            </p>
            <Button
              onClick={handleLogin}
              className="bg-[#002FA7] hover:bg-[#00227A] text-white font-medium px-8 py-6 text-lg rounded flex items-center gap-2"
              data-testid="hero-login-btn"
            >
              Get Started <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 border-t border-zinc-200">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 mb-4">
            FEATURES
          </p>
          <h3 className="text-2xl tracking-tight font-bold text-zinc-900 mb-12">
            Everything you need for intelligent scheduling
          </h3>
          
          <div className="grid md:grid-cols-2 gap-px bg-zinc-200">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white p-8">
                  <div className="w-10 h-10 bg-zinc-100 rounded flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-zinc-700" />
                  </div>
                  <h4 className="text-lg tracking-tight font-medium text-zinc-900 mb-2">
                    {feature.title}
                  </h4>
                  <p className="text-sm text-zinc-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Rules Section */}
      <section className="py-20 border-t border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 mb-4">
                SCHEDULING RULES
              </p>
              <h3 className="text-2xl tracking-tight font-bold text-zinc-900 mb-6">
                Configure rules that matter to your team
              </h3>
              <ul className="space-y-4">
                {[
                  "Maximum hours per week per employee",
                  "Minimum rest between consecutive shifts",
                  "Skill and role requirements for shifts",
                  "Fair distribution of weekend shifts",
                  "Per-person shift type preferences",
                  "Minimum staff per shift requirements"
                ].map((rule, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#002FA7] rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm text-zinc-600">{rule}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-zinc-50 border border-zinc-200 p-8 rounded">
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-zinc-200">
                  <span className="text-sm font-medium text-zinc-900">Max Hours/Week</span>
                  <span className="text-sm tabular-nums text-zinc-600">40 hours</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-zinc-200">
                  <span className="text-sm font-medium text-zinc-900">Min Rest Period</span>
                  <span className="text-sm tabular-nums text-zinc-600">8 hours</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-zinc-200">
                  <span className="text-sm font-medium text-zinc-900">Min Staff/Shift</span>
                  <span className="text-sm tabular-nums text-zinc-600">2 people</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-sm font-medium text-zinc-900">Weekend Balance</span>
                  <span className="text-xs uppercase tracking-wide font-medium text-emerald-600">Enabled</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#002FA7] mb-4">
            PRICING
          </p>
          {pricing?.introductory && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-3 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold uppercase tracking-wide" data-testid="landing-intro-badge">
              <Sparkles className="w-3 h-3" /> {pricing.introductory_label || "Introductory price"}
            </div>
          )}
          <h3 className="text-2xl tracking-tight font-bold text-zinc-900 mb-2" data-testid="landing-price">
            {pricing ? `${fmtPrice(pricing.price)}/month — 30-day free trial, no card required` : (
              <span className="inline-block h-7 w-64 bg-zinc-200 rounded animate-pulse" data-testid="landing-price-skeleton" />
            )}
          </h3>
          {pricing?.introductory && pricing.introductory_note && (
            <p className="text-xs text-amber-700 max-w-xl mx-auto mb-2" data-testid="landing-intro-note">
              {pricing.introductory_note}
            </p>
          )}
          <p className="text-sm text-zinc-600 mb-8">
            Sign in with Google to start your free 30-day trial. No credit card required.
          </p>
          <Button
            onClick={handleLogin}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white font-medium px-8 py-6 text-lg rounded inline-flex items-center gap-2"
            data-testid="cta-login-btn"
          >
            Start Free Trial <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 py-8">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs text-zinc-500">
            &copy; 2026 roster.ai — Built with GPT-5.2
          </p>
          <div className="flex items-center gap-4 text-xs text-zinc-500">
            <a href="/contact" className="hover:text-zinc-900 transition-colors" data-testid="footer-contact-link">
              Contact
            </a>
            <span>Powered by Emergent</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
